import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Wifi, WifiOff, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api, listFGs, singleLabelPdfUrl, coilLabelTsplUrl } from "@/lib/api";
import PrintButton from "@/components/PrintButton";
import LabelPrinterStatus from "@/components/LabelPrinterStatus";
import { useAuth } from "@/contexts/AuthContext";

const t = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—");
const dt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const kg = (v) => (v == null ? "—" : Number(v).toFixed(1));
const cell = "px-3 py-2 whitespace-nowrap";

export const ScaleLinkStatus = ({ status }) => (
  <span data-testid="fg-scale-link" data-online={!!status?.online}
    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${status?.online ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
    {status?.online ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
    {status?.online ? "Floor scale online" : `Offline – last seen ${status?.last_seen ? dt(status.last_seen) : "never"}`}
  </span>
);

const Exceptions = ({ rows, canResolve }) => {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (id) => api.post(`/fg-scale/exceptions/${id}/resolve`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["fg-scale"] }); toast.success("Marked as resolved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't resolve"),
  });
  if (!rows.length) return null;
  return (
    <section data-testid="fg-exceptions" className="border border-amber-300 bg-amber-50 rounded-sm p-4 space-y-2">
      <h3 className="text-sm font-bold text-amber-900 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Needs review – lines not turned into FG ({rows.length})</h3>
      {rows.map((e, i) => (
        <div key={e.id} data-testid={`fg-exception-${i}`} className="flex flex-wrap items-center gap-3 text-sm bg-white border border-amber-200 rounded-sm px-3 py-2">
          <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm bg-amber-200 text-amber-900">{e.kind === "conflict" ? "FG ID conflict" : "Unreadable line"}</span>
          <code className="font-mono text-xs text-slate-800">{e.line}</code>
          <span className="text-xs text-slate-600 flex-1">{e.detail}</span>
          <span className="text-xs text-slate-400">{dt(e.received_at)}</span>
          {canResolve && <button data-testid={`fg-exception-resolve-${i}`} onClick={() => mut.mutate(e.id)} className="text-xs font-semibold text-green-700 hover:underline">Resolve</button>}
        </div>
      ))}
    </section>
  );
};

const FG = () => {
  const { hasModule } = useAuth();
  const [f, setF] = useState({ date: "", sku: "", source: "", review: false });
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const { data: fgs = [] } = useQuery({ queryKey: ["fgs-all"], queryFn: () => listFGs(), refetchInterval: 15_000 });
  const { data: status } = useQuery({ queryKey: ["fg-scale", "status"], queryFn: async () => (await api.get("/fg-scale/status")).data, refetchInterval: 15_000 });
  const { data: exc = [] } = useQuery({ queryKey: ["fg-scale", "exc"], queryFn: async () => (await api.get("/fg-scale/exceptions")).data, refetchInterval: 15_000 });

  const skus = useMemo(() => [...new Set(fgs.map((x) => x.sku_name).filter(Boolean))].sort(), [fgs]);
  const rows = useMemo(() => fgs
    .filter((x) => (!f.date || (x.production_date || "").startsWith(f.date)) && (!f.sku || x.sku_name === f.sku)
      && (!f.source || (x.source || "Manual") === f.source) && (!f.review || x.needs_review))
    .sort((a, b) => String(b.scale_received_at || b.created_at || "").localeCompare(String(a.scale_received_at || a.created_at || ""))), [fgs, f]);

  return (
    <div data-testid="fg-page" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-200 pb-3">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold mb-1">Finished Goods</p>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900">FG Production · Floor Scale</h2>
          <p className="text-xs text-slate-500 mt-1">Pallets are created by the floor-scale touch screen. The ERP only stores them and can reprint the same label.</p>
        </div>
        <div className="flex items-center gap-2">
          <ScaleLinkStatus status={status} />
          <LabelPrinterStatus testId="fg-label-printer-status" />
        </div>
      </div>

      <Exceptions rows={exc} canResolve={hasModule("inventory.fg_edit")} />

      <div className="flex flex-wrap items-center gap-2">
        <input data-testid="fg-filter-date" type="date" value={f.date} onChange={set("date")} className="h-9 px-2 border border-slate-300 rounded-sm text-sm" />
        <select data-testid="fg-filter-sku" value={f.sku} onChange={set("sku")} className="h-9 px-2 border border-slate-300 rounded-sm text-sm bg-white">
          <option value="">All SKUs</option>{skus.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select data-testid="fg-filter-source" value={f.source} onChange={set("source")} className="h-9 px-2 border border-slate-300 rounded-sm text-sm bg-white">
          <option value="">All sources</option><option>Floor Scale 1</option><option>Legacy</option><option>Manual</option>
        </select>
        <label className="inline-flex items-center gap-1.5 text-sm h-9 px-3 border border-slate-300 rounded-sm bg-white cursor-pointer">
          <input data-testid="fg-filter-review" type="checkbox" checked={f.review} onChange={set("review")} /> Needs review only
          {status?.review_fgs > 0 && <span className="ml-1 text-[10px] font-bold px-1.5 rounded-full bg-amber-400 text-amber-950">{status.review_fgs}</span>}
        </label>
        <span data-testid="fg-count" className="ml-auto text-xs text-slate-500">{rows.length} pallets · {kg(rows.reduce((a, x) => a + (x.weight_kg || 0), 0))} kg net</span>
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] tracking-[0.15em] uppercase text-slate-500">
            <tr>{["FG ID", "SKU", "Prod. date", "Net kg", "Gross kg", "Tare kg", "Received", "Source", "Review", "Status", ""].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && <tr><td colSpan={11} className="px-3 py-10 text-center text-slate-400">No FG pallets match.</td></tr>}
            {rows.map((x) => (
              <tr key={x.id} data-testid={`fg-row-${x.coil_id}`} className={x.needs_review ? "bg-amber-50/60" : ""}>
                <td className={`${cell} font-mono font-bold text-slate-900`}>{x.coil_id}{x.scale_raw_number != null && <span className="ml-1 text-[10px] font-normal text-slate-400">(scale {x.scale_raw_number})</span>}</td>
                <td className={cell}>{x.sku_name || "—"}</td>
                <td className={cell}>{x.production_date || "—"}</td>
                <td className={`${cell} font-mono`}>{kg(x.weight_kg)}</td>
                <td className={`${cell} font-mono`}>{kg(x.gross_weight_kg)}</td>
                <td className={`${cell} font-mono`}>{kg(x.tare_weight_kg)}</td>
                <td className={`${cell} text-xs text-slate-500`}>{x.scale_received_at ? `${dt(x.scale_received_at)}` : t(x.created_at)}</td>
                <td className={cell}><span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-700">{x.source || "Manual"}</span></td>
                <td className={cell} data-testid={`fg-review-${x.coil_id}`}>
                  {x.needs_review
                    ? <span title={(x.review_reasons || []).join("\n")} className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800"><AlertTriangle className="h-4 w-4 text-amber-500" /> Needs review</span>
                    : (x.review_reasons?.length ? <span className="inline-flex items-center gap-1 text-xs text-green-700"><CheckCircle2 className="h-4 w-4" /> Corrected</span> : "—")}
                </td>
                <td className={`${cell} text-xs uppercase`}>{(x.status || "").replace(/_/g, " ")}</td>
                <td className={cell}>
                  <PrintButton testId={`fg-reprint-${x.coil_id}`} variant="outline" size="sm" tspl={coilLabelTsplUrl(x.id)} pdf={singleLabelPdfUrl(x.id)} label={`${x.coil_id} label`}>Reprint</PrintButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FG;
